import { random } from 'lodash';
import { z } from 'zod';
import { legalTransactionRelationshipRealEstatePropertyTypeSchema } from './ILegalTransactionRelationshipRealEstatePropertyType';
import { landParcelEntrySchema } from '../../IRealEstate';
import { realEstatePurchaseContractSchema } from './IRealEstatePurchaseContract';

/**
 * Eigentümer eines Grundstücks (Pfad B: Asset → LegalClientAsset, Rolle
 * "Eigentümer"). Wird mit dem Vorgang in einem Zug gespeichert — das Backend
 * legt/aktualisiert dazu das zum Grundstück gehörende Asset und dessen
 * LegalClientAsset-Einträge.
 */
export const legalTransactionRelationshipRealEstateOwnerSchema = z.object({
  idLegalClientAsset: z.number().optional(),
  idLegalClient: z.number(),
  idLegalClientAssetRole: z.number(),
  share: z.number().nullable().optional(),
  LegalClientAssetRole: z.string().nullable().optional(),
});

export type ILegalTransactionRelationshipRealEstateOwner = z.infer<
  typeof legalTransactionRelationshipRealEstateOwnerSchema
>;

export const legalTransactionRelationshipRealEstateSchema = z.object({
  // ── Adresse ───────────────────────────────────────────────────────────────
  Street: z.string().nullable(),
  StreetNr: z.string().nullable(),
  StreetAditional: z.string().nullable(),
  Latitude: z.number().nullable(),
  Longitude: z.number().nullable(),
  idPostcode: z.number().nullable(),
  Postcode: z.string().optional(),
  City: z.string().optional(),

  // ── Objektdaten ───────────────────────────────────────────────────────────
  Area: z.number().nullable(),
  BuildWith: z.string().nullable(),
  Defect: z.string().nullable(),
  EasementText: z.string().nullable(),
  Particularities: z.string().nullable(),

  // ── Grundbuch / Kataster ──────────────────────────────────────────────────
  idCourt: z.number().nullable(),
  LandParcelEntryArray: z.array(landParcelEntrySchema),

  // ── Klassifizierung ───────────────────────────────────────────────────────
  idRealEstateUseType: z.number().min(1),
  isBuild: z.boolean(),
  hasApartment: z.boolean(),
  hasPartialArea: z.boolean(),
  isMeasured: z.boolean(),
  hasEasement: z.boolean(),

  // ── Anbauten / PropertyTypes (Garage etc.) ────────────────────────────────
  PropertyTypeArray: z.array(
    legalTransactionRelationshipRealEstatePropertyTypeSchema
  ),

  // ── Eigentümer + Hausverwalter ────────────────────────────────────────────
  BuildingManager_idLegalClient: z.number().nullable(),
  OwnerArray: z
    .array(legalTransactionRelationshipRealEstateOwnerSchema)
    .optional(),

  // ── DB-Verweise ───────────────────────────────────────────────────────────
  idAddress: z.number().nullable(),
  idRealEstate: z.number(),
  idRelationship: z.number().nullable(),

  // ── Kaufvertrags-Datensätze (kaufvertrags-spezifisch, je Vorgang) ─────────
  PurchaseContractArray: z.array(realEstatePurchaseContractSchema).optional(),
});

export type ILegalTransactionRelationshipRealEstate = z.infer<
  typeof legalTransactionRelationshipRealEstateSchema
>;

export const initRealEstate = (): ILegalTransactionRelationshipRealEstate => ({
  idRealEstate: -random(1, Number.MAX_SAFE_INTEGER),
  Street: null,
  StreetNr: null,
  StreetAditional: null,
  idPostcode: null,
  Latitude: null,
  Longitude: null,
  Area: null,
  BuildWith: null,
  Defect: null,
  EasementText: null,
  Particularities: null,
  idCourt: null,

  hasApartment: false,
  hasEasement: false,
  hasPartialArea: false,
  idAddress: null,

  idRealEstateUseType: 1,
  idRelationship: null,
  isBuild: false,
  isMeasured: false,

  LandParcelEntryArray: [],
  PropertyTypeArray: [],

  BuildingManager_idLegalClient: null,
  OwnerArray: [],

  PurchaseContractArray: [],
});
